import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Phone, Mail, MapPin, MessageCircle, Send, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const services = [
  "GST Registration & Filing",
  "ITR Filing",
  "Tax Compliance & Advisory",
  "Company Registration / ROC",
  "Accounting & Bookkeeping",
  "Other",
];

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) =>
    setForm((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service || !form.message) {
      toast.error("Please fill in all fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you! We'll get back to you within 24 hours.");
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Something went wrong. Please try calling us directly at 73536 26623."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      className="relative border-b border-border bg-background py-24 md:py-32"
      data-testid="contact-section"
    >
      <div className="container mx-auto max-w-7xl px-6 md:px-12">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-12 md:gap-16">
          <div className="md:col-span-5">
            <div className="mb-4 flex items-center gap-3">
              <span className="h-px w-10 bg-primary" />
              <span className="font-[Outfit] text-xs font-bold uppercase tracking-[0.2em] text-primary">
                Contact
              </span>
            </div>
            <h2 className="text-4xl font-extrabold leading-tight tracking-tight text-foreground md:text-5xl">
              Let&apos;s simplify<br />
              <span className="italic text-primary">your finances.</span>
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-muted-foreground md:text-lg">
              Reach out for a free 15-minute consultation. Whether it&apos;s a
              one-time filing or ongoing compliance, we&apos;re ready to help.
            </p>

            <div className="mt-10 space-y-1">
              <a
                href="tel:7353626623"
                className="group flex items-center justify-between border-b border-border py-5 hover:border-primary"
                data-testid="contact-phone-link"
              >
                <div className="flex items-center gap-4">
                  <div className="flex h-11 w-11 items-center justify-center border border-border bg-white group-hover:border-primary group-hover:bg-primary group-hover:text-primary-foreground">
                    <Phone className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-muted-foreground">
                      Call us
                    </div>
                    <div className="font-[Outfit] text-lg font-bold text-foreground">
                      +91 73536 26623
                    </div>
                  </div>
                </div>
              </a>

              <a
                href="https://wa.me/917353626623?text=Hi%20NP%20Associates%2C%20I%20need%20help%20with%20"
                target="_blank"
                rel="noreferrer noopener"
                className="group flex items-center justify-between border-b border-border py-5 hover:border-primary"
                data-testid="contact-whatsapp-link"
              >
                <div className="flex items-center gap-4">
                  <div className="flex h-11 w-11 items-center justify-center border border-border bg-white group-hover:border-primary group-hover:bg-primary group-hover:text-primary-foreground">
                    <MessageCircle className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-muted-foreground">
                      WhatsApp
                    </div>
                    <div className="font-[Outfit] text-lg font-bold text-foreground">
                      Chat instantly
                    </div>
                  </div>
                </div>
              </a>

              <a
                href="mailto:tmpn6@outlook.com"
                className="group flex items-center justify-between border-b border-border py-5 hover:border-primary"
                data-testid="contact-email-link"
              >
                <div className="flex items-center gap-4">
                  <div className="flex h-11 w-11 items-center justify-center border border-border bg-white group-hover:border-primary group-hover:bg-primary group-hover:text-primary-foreground">
                    <Mail className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-muted-foreground">
                      Email
                    </div>
                    <div className="font-[Outfit] text-lg font-bold text-foreground">
                      tmpn6@outlook.com
                    </div>
                  </div>
                </div>
              </a>

              <div className="flex items-center justify-between py-5">
                <div className="flex items-center gap-4">
                  <div className="flex h-11 w-11 items-center justify-center border border-border bg-white">
                    <MapPin className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-muted-foreground">
                      Office
                    </div>
                    <div className="font-[Outfit] text-base font-bold leading-snug text-foreground">
                      Near Sarang College, Thammanna Layout,<br />
                      Gulur, Tumakuru – 572118, Karnataka
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="border border-border bg-secondary/30 p-8 md:col-span-7 md:p-10"
            data-testid="contact-form"
          >
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="name" className="font-[Outfit] text-xs font-bold uppercase tracking-widest">
                  Full Name
                </Label>
                <Input
                  id="name"
                  data-testid="contact-input-name"
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Ravi Kumar"
                  className="h-12 rounded-none border-border bg-white focus-visible:ring-primary"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="font-[Outfit] text-xs font-bold uppercase tracking-widest">
                  Email Address
                </Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="contact-input-email"
                  value={form.email}
                  onChange={set("email")}
                  placeholder="ravi@example.com"
                  className="h-12 rounded-none border-border bg-white focus-visible:ring-primary"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone" className="font-[Outfit] text-xs font-bold uppercase tracking-widest">
                  Phone Number
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  data-testid="contact-input-phone"
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="+91 98765 43210"
                  className="h-12 rounded-none border-border bg-white focus-visible:ring-primary"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="service" className="font-[Outfit] text-xs font-bold uppercase tracking-widest">
                  Service Required
                </Label>
                <Select value={form.service} onValueChange={set("service")}>
                  <SelectTrigger
                    id="service"
                    data-testid="contact-select-service"
                    className="h-12 rounded-none border-border bg-white focus:ring-primary"
                  >
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    {services.map((s) => (
                      <SelectItem
                        key={s}
                        value={s}
                        className="rounded-none"
                        data-testid={`contact-service-option-${s.replace(/\s+/g, "-").toLowerCase()}`}
                      >
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="message" className="font-[Outfit] text-xs font-bold uppercase tracking-widest">
                  Tell us about your requirement
                </Label>
                <Textarea
                  id="message"
                  data-testid="contact-input-message"
                  value={form.message}
                  onChange={set("message")}
                  rows={5}
                  placeholder="I need help with GST registration for my new business..."
                  className="rounded-none border-border bg-white focus-visible:ring-primary"
                  required
                />
              </div>
            </div>

            <div className="mt-8 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
              <p className="text-xs text-muted-foreground">
                Your info stays private. We respond within 24 hours.
              </p>
              <Button
                type="submit"
                disabled={loading}
                className="h-13 group rounded-none bg-primary px-8 py-4 font-[Outfit] font-semibold uppercase tracking-wider hover:bg-primary/90"
                data-testid="contact-submit-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending
                  </>
                ) : (
                  <>
                    Send Inquiry
                    <Send className="ml-2 h-4 w-4 transition group-hover:translate-x-0.5" />
                  </>
                )}
              </Button>
            </div>
          </motion.form>
        </div>

        {/* Google Maps embed */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mt-16 border border-border"
          data-testid="contact-map"
        >
          <div className="flex flex-col items-start justify-between gap-2 border-b border-border bg-secondary/40 px-6 py-4 sm:flex-row sm:items-center">
            <div className="flex items-center gap-3">
              <MapPin className="h-4 w-4 text-primary" />
              <span className="font-[Outfit] text-sm font-bold uppercase tracking-widest text-foreground">
                Find us on the map
              </span>
            </div>
            <a
              href="https://www.google.com/maps/dir/?api=1&destination=NP+Associates+Thammanna+Layout+Gulur+Tumakuru+572118"
              target="_blank"
              rel="noreferrer noopener"
              className="font-[Outfit] text-xs font-bold uppercase tracking-widest text-primary hover:text-foreground"
              data-testid="contact-directions-link"
            >
              Get Directions →
            </a>
          </div>
          <iframe
            title="NP Associates office location — Gulur, Tumakuru"
            src="https://www.google.com/maps?q=NP+Associate+Gulur+Tumakuru&output=embed"
            width="100%"
            height="420"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            className="block h-[360px] w-full border-0 md:h-[420px]"
            allowFullScreen
          />
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;
